import React, {useCallback} from 'react';
import {Subject} from "rxjs";
import {AuthorizationInfo} from "../../domain";
import {TreatmentHoldInput} from "./TreatmentHoldFormModal";
import {useObservable} from "../../../../rxsupport";
import {useForm} from "react-hook-form";
import {showToast} from "common";
import {ReportModal} from "../../form-components/ReportModal";
import {TreatmentHoldForm} from "./TreatmentHoldForm";
import {
    emptyTreatmentHoldInput,
    TreatmentHoldSubmitInput,
    shouldRedirectToStaffingPlan,
    toTreatmentHoldSubmitPayload,
} from "./treatmentHoldDefaults";
import {useLatestTreatmentHoldInput} from "./useLatestTreatmentHoldInput";

type TreatmentHoldFromAuthModalProps = {
    visible$: Subject<boolean>;
    authorization: AuthorizationInfo;
    onFinished: (formValues: TreatmentHoldSubmitInput | null) => Promise<void>;
    onRedirectToStaffingPlan?: () => void;
}

export function TreatmentHoldFromAuthModal({
                                                     authorization,
                                                     visible$,
                                                     onFinished,
                                                     onRedirectToStaffingPlan,
                                                 }: TreatmentHoldFromAuthModalProps) {
    const visible = useObservable(visible$, false);

    const methods = useForm<TreatmentHoldInput>({
        defaultValues: emptyTreatmentHoldInput(authorization),
    });

    useLatestTreatmentHoldInput(visible, methods, authorization.guid);

    const handleFinished = useCallback(async (formValues: TreatmentHoldInput | null) => {
        if (formValues == null) {
            return;
        }
        const payload = toTreatmentHoldSubmitPayload(formValues, authorization);
        await onFinished(payload);
        if (shouldRedirectToStaffingPlan(formValues, authorization) && onRedirectToStaffingPlan) {
            onRedirectToStaffingPlan();
        }
    }, [onFinished, onRedirectToStaffingPlan, authorization]);

    const onCancel = useCallback(async () => {
        showToast.warning(`Submission for Treatment Hold is cancelled`);
        methods.reset(emptyTreatmentHoldInput(authorization));
        visible$.next(false);
    }, [methods, visible$, authorization]);

    return (
        <ReportModal visible={visible} title={'Treatment Hold'} onClose={onCancel}>
            <TreatmentHoldForm
                methods={methods}
                visible$={visible$}
                onFinished={handleFinished}
                onCancel={onCancel}
                authorizationGuid={authorization.guid}
                authorization={authorization}
                externalLoading={false}
            />
        </ReportModal>
    );
}
